import React, { useState, useEffect } from 'react';
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  InputAdornment,
  Popover,
  Button,
} from '@mui/material';
import './EditCoupen.css';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

// Custom Calendar Component
const CustomCalendar = ({ value, onChange, minDate, onClose }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Helper function to parse YYYY-MM-DD string to Date object without timezone conversion
  const parseDateString = dateString => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  const [selectedDate, setSelectedDate] = useState(value ? parseDateString(value) : null);

  useEffect(() => {
    setSelectedDate(value ? parseDateString(value) : null);
  }, [value]);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Helper function to format date as YYYY-MM-DD without timezone conversion
  const formatDateToYYYYMMDD = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const handleDateClick = date => {
    if (!date) return;

    if (minDate) {
      const [minYear, minMonth, minDay] = minDate.split('-').map(Number);
      const minDateObj = new Date(minYear, minMonth - 1, minDay); // month is 0-indexed

      // Compare dates by setting both to midnight for accurate comparison
      const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const minMidnight = new Date(
        minDateObj.getFullYear(),
        minDateObj.getMonth(),
        minDateObj.getDate()
      );

      if (dateMidnight < minMidnight) return;
    }

    setSelectedDate(date);
    const formattedDate = formatDateToYYYYMMDD(date);
    onChange(formattedDate);
  };

  const handleMonthChange = direction => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleYearChange = direction => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(currentDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
    const formattedDate = formatDateToYYYYMMDD(today);
    onChange(formattedDate);
  };

  const handleClear = () => {
    setSelectedDate(null);
    onChange('');
  };

  const isDateDisabled = date => {
    if (!date || !minDate) return false;

    // Parse minDate string (YYYY-MM-DD) to create a comparable date
    const [minYear, minMonth, minDay] = minDate.split('-').map(Number);
    const minDateObj = new Date(minYear, minMonth - 1, minDay); // month is 0-indexed

    // Compare dates by setting both to midnight for accurate comparison
    const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minMidnight = new Date(
      minDateObj.getFullYear(),
      minDateObj.getMonth(),
      minDateObj.getDate()
    );

    // Disable dates that are less than minDate (minDate itself is allowed)
    return dateMidnight < minMidnight;
  };

  const isToday = date => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = date => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="edit-coupon-custom-calendar">
      <div className="edit-coupon-calendar-header">
        <div className="edit-coupon-month-year-selector">
          <div className="edit-coupon-month-selector">
            <span className="edit-coupon-month-year-text">
              {months[currentDate.getMonth()]}, {currentDate.getFullYear()}
            </span>
            <div className="edit-coupon-navigation-buttons">
              <IconButton
                size="small"
                onClick={() => handleMonthChange(-1)}
                className="edit-coupon-nav-button"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleMonthChange(1)}
                className="edit-coupon-nav-button"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
        <div className="edit-coupon-year-navigation">
          <IconButton
            size="small"
            onClick={() => handleYearChange(-1)}
            className="edit-coupon-nav-button"
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleYearChange(1)}
            className="edit-coupon-nav-button"
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="edit-coupon-calendar-days-header">
        {daysOfWeek.map(day => (
          <div key={day} className="edit-coupon-day-header">
            {day}
          </div>
        ))}
      </div>

      <div className="edit-coupon-calendar-grid">
        {days.map((date, index) => (
          <div
            key={index}
            className={`edit-coupon-calendar-day ${!date ? 'empty' : ''} ${
              isSelected(date) ? 'selected' : ''
            } ${isToday(date) ? 'today' : ''} ${isDateDisabled(date) ? 'disabled' : ''}`}
            onClick={() => handleDateClick(date)}
          >
            {date ? date.getDate() : ''}
          </div>
        ))}
      </div>

      <div className="edit-coupon-calendar-footer">
        <Button onClick={handleClear} className="edit-coupon-calendar-clear-btn" size="small">
          Clear
        </Button>
        <Button onClick={handleToday} className="edit-coupon-calendar-today-btn" size="small">
          Today
        </Button>
      </div>
    </div>
  );
};

const EditCoupen = ({ onClose, onSuccess, couponId, isOpen }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    coupon_type: 'teacher_enrollment',
    discount_percentage: '100',
    valid_from: '',
    valid_until: '',
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [fromCalendarAnchorEl, setFromCalendarAnchorEl] = useState(null);
  const [untilCalendarAnchorEl, setUntilCalendarAnchorEl] = useState(null);

  const couponTypes = [{ value: 'teacher_enrollment', label: 'Instructor Enrollment' }];

  // Helper function to get today's date in YYYY-MM-DD format without timezone conversion
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get the next day after a given date
  const getNextDayAfter = dateString => {
    if (!dateString) return getTodayDateString();

    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    date.setDate(date.getDate() + 1); // Add one day

    const nextYear = date.getFullYear();
    const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
    const nextDay = String(date.getDate()).padStart(2, '0');
    return `${nextYear}-${nextMonth}-${nextDay}`;
  };

  // Helper function to convert UTC date to local for display
  const convertUTCToLocal = dateString => {
    if (!dateString) return '';

    // Parse the UTC date string
    const utcDate = new Date(dateString);

    // Convert to local timezone
    const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

    // Format as YYYY-MM-DD
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Helper function to convert local date to UTC for API calls
  const convertLocalToUTC = dateString => {
    if (!dateString) return '';

    // Parse the date string and create a local date
    const localDate = new Date(dateString);

    // Convert to UTC by adding timezone offset
    const utcDate = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);

    // Format as YYYY-MM-DD
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Reset form data when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        code: '',
        name: '',
        description: '',
        coupon_type: 'teacher_enrollment',
        discount_percentage: '',
        valid_from: '',
        valid_until: '',
      });
      setFetching(false);
      setError(null);
    }
  }, [isOpen]);

  // Fetch coupon data on component mount
  useEffect(() => {
    const fetchCouponData = async () => {
      if (!couponId || !isOpen) return;

      try {
        setFetching(true);
        setError(null);
        const response = await axiosInstance.get(`/admin/coupon/${couponId}`);
        const couponData = response.data;

        // Convert UTC dates to local timezone for form
        setFormData({
          code: couponData.code || '',
          name: couponData.name || '',
          description: couponData.description || '',
          coupon_type: couponData.coupon_type || '',
          discount_percentage: couponData.discount_percentage || '',
          valid_from: convertUTCToLocal(couponData.valid_from),
          valid_until: convertUTCToLocal(couponData.valid_until),
        });
      } catch (error) {
        console.error('Error fetching coupon data:', error);
        setError('Failed to load coupon data. Please try again.');
      } finally {
        setFetching(false);
      }
    };

    fetchCouponData();
  }, [couponId, isOpen]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Date picker event handlers
  const handleFromCalendarClick = event => {
    setFromCalendarAnchorEl(event.currentTarget);
  };

  const handleFromCalendarClose = () => {
    setFromCalendarAnchorEl(null);
  };

  const handleUntilCalendarClick = event => {
    setUntilCalendarAnchorEl(event.currentTarget);
  };

  const handleUntilCalendarClose = () => {
    setUntilCalendarAnchorEl(null);
  };

  const handleFromDateChange = date => {
    setFormData({ ...formData, valid_from: date });
    handleFromCalendarClose();
  };

  const handleUntilDateChange = date => {
    setFormData({ ...formData, valid_until: date });
    handleUntilCalendarClose();
  };

  // Clear valid_until if it becomes invalid when valid_from changes
  useEffect(() => {
    if (formData.valid_from && formData.valid_until) {
      const fromDate = new Date(formData.valid_from);
      const untilDate = new Date(formData.valid_until);

      // If valid_until is less than or equal to valid_from, clear it
      if (untilDate <= fromDate) {
        setFormData(prev => ({ ...prev, valid_until: '' }));
      }
    }
  }, [formData.valid_from]);

  const formatDateForDisplay = dateString => {
    if (!dateString) return '';

    // Parse YYYY-MM-DD format without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const fromOpen = Boolean(fromCalendarAnchorEl);
  const untilOpen = Boolean(untilCalendarAnchorEl);

  const isFormValid = Boolean(
    formData.code.trim() &&
      formData.name.trim() &&
      formData.description.trim() &&
      formData.coupon_type &&
      formData.discount_percentage !== '' &&
      formData.valid_from &&
      formData.valid_until
  );

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const couponData = {
        ...formData,
        discount_percentage: parseFloat(formData.discount_percentage),
        valid_from: convertLocalToUTC(formData.valid_from),
        valid_until: convertLocalToUTC(formData.valid_until),
        status: 'active',
      };

      const response = await axiosInstance.patch(`/admin/coupon/${couponId}`, couponData);

      if (onSuccess) {
        onSuccess(response.data);
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error updating coupon:', err);

      // Parse API error response
      let errorMessage = 'An error occurred while updating the coupon.';

      if (err.response && err.response.data) {
        const errorData = err.response.data;

        // Handle detail as array or string, and other known fields
        if (errorData.detail && Array.isArray(errorData.detail)) {
          // Extract error messages from the detail array
          const errorMessages = errorData.detail.map(error => {
            if (error.msg) {
              // Format field-specific errors more user-friendly
              if (error.loc && Array.isArray(error.loc) && error.loc.length > 1) {
                const fieldName = error.loc[error.loc.length - 1];
                const formattedFieldName = fieldName
                  .split('_')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                return `${formattedFieldName}: ${error.msg}`;
              }
              return error.msg;
            }
            return 'Validation error';
          });
          errorMessage = errorMessages.join('. ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (fetching) {
    return (
      <div className="edit-coupon-modal-overlay">
        <div className="edit-coupon-modal-container">
          <div className="edit-coupon-loading">
            <p>Loading coupon data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-coupon-modal-overlay">
      <div className="edit-coupon-modal-container">
        <div className="edit-coupon-modal-header">
          <Box className="edit-coupon-modal-title">Edit Coupon</Box>
          <button className="edit-coupon-close-btn" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="edit-coupon-divider"></div>
        <form onSubmit={handleSubmit} className="edit-coupon-modal-form">
          <div className="edit-coupon-modal-content">
            <div className="edit-coupon-form-field">
              <Box className="edit-coupon-form-label">Coupon Code</Box>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="Enter coupon code (e.g., SUMMER2025)"
                className="edit-coupon-form-input"
                required
              />
            </div>

            <div className="edit-coupon-form-field">
              <Box className="edit-coupon-form-label">Coupon Name</Box>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter coupon name"
                className="edit-coupon-form-input"
                required
              />
            </div>

            <div className="edit-coupon-form-field">
              <Box className="edit-coupon-form-label">Description</Box>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter coupon description"
                className="edit-coupon-form-textarea"
                rows="3"
                required
              />
            </div>

            <div className="edit-coupon-form-field">
              <Box className="edit-coupon-form-label">Coupon Type</Box>
              <FormControl fullWidth>
                <Select
                  name="coupon_type"
                  value={formData.coupon_type}
                  onChange={handleChange}
                  displayEmpty
                  className="edit-coupon-form-input"
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={selected => {
                    if (!selected) return 'Select coupon type';
                    const selectedType = couponTypes.find(type => type.value === selected);
                    return selectedType ? selectedType.label : selected;
                  }}
                  required
                  inputProps={{
                    className: 'edit-coupon-mui-select',
                  }}
                >
                  <MenuItem value="" disabled className="edit-coupon-mui-menu-item">
                    Select coupon type
                  </MenuItem>
                  {couponTypes.map(type => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                      className="edit-coupon-mui-menu-item"
                    >
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="edit-coupon-form-field">
              <Box className="edit-coupon-form-label">Discount Percentage</Box>
              <div className="edit-coupon-discount-input-container">
                <input
                  type="number"
                  name="discount_percentage"
                  value={formData.discount_percentage}
                  onChange={handleChange}
                  placeholder="0"
                  className="edit-coupon-form-input edit-coupon-discount-input"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  readOnly
                  disabled
                />
                <span className="edit-coupon-discount-symbol">%</span>
              </div>
            </div>

            <div className="edit-coupon-date-fields-container">
              <div className="edit-coupon-form-field">
                <Box className="edit-coupon-form-label">Valid From</Box>
                <div className="edit-coupon-date-picker-container">
                  <div className="edit-coupon-date-input-wrapper" onClick={handleFromCalendarClick}>
                    <input
                      type="text"
                      value={formatDateForDisplay(formData.valid_from)}
                      placeholder="Select start date"
                      className="edit-coupon-form-input edit-coupon-date-display-input"
                      readOnly
                    />
                    <CalendarTodayIcon className="edit-coupon-calendar-icon" />
                  </div>

                  <Popover
                    open={fromOpen}
                    anchorEl={fromCalendarAnchorEl}
                    onClose={handleFromCalendarClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      elevation: 8,
                      sx: {
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        mt: 1,
                      },
                    }}
                  >
                    <CustomCalendar
                      value={formData.valid_from}
                      onChange={handleFromDateChange}
                      minDate={getTodayDateString()}
                    />
                  </Popover>
                </div>
              </div>

              <div className="edit-coupon-form-field">
                <Box className="edit-coupon-form-label">Valid Until</Box>
                <div className="edit-coupon-date-picker-container">
                  <div
                    className="edit-coupon-date-input-wrapper"
                    onClick={handleUntilCalendarClick}
                  >
                    <input
                      type="text"
                      value={formatDateForDisplay(formData.valid_until)}
                      placeholder={
                        formData.valid_from
                          ? 'Select end date (must be after start date)'
                          : 'Select end date'
                      }
                      className="edit-coupon-form-input edit-coupon-date-display-input"
                      readOnly
                    />
                    <CalendarTodayIcon className="edit-coupon-calendar-icon" />
                  </div>

                  <Popover
                    open={untilOpen}
                    anchorEl={untilCalendarAnchorEl}
                    onClose={handleUntilCalendarClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      elevation: 8,
                      sx: {
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        mt: 1,
                      },
                    }}
                  >
                    <CustomCalendar
                      value={formData.valid_until}
                      onChange={handleUntilDateChange}
                      minDate={
                        formData.valid_from
                          ? getNextDayAfter(formData.valid_from)
                          : getTodayDateString()
                      }
                    />
                  </Popover>
                </div>
              </div>
            </div>
          </div>

          <div className="edit-coupon-modal-footer">
            <button
              type="button"
              className="edit-coupon-cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-coupon-save-btn"
              disabled={loading || !isFormValid}
            >
              {loading ? 'Updating...' : 'Update Coupon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCoupen;
